import { For, useContext, Show, type JSX } from 'solid-js';
import clsx from 'clsx';
import type { ProductSpecificationProps } from './product-specifications-types';
import { Specification } from '../product-types';
import { AppContext } from '../../../app-context-provider/app-context-provider';
import theme from '../../../style/theme';
import { ProductSpecificationsMobile } from './product-specifications-mobile';
import { AddToBasketButton } from '../../basket/basket-components';

const CELL_PADDING = 'px-cvi-6 py-cvi-5';

const HeaderCell = (props: { children?: JSX.Element | string; class?: string }) => (
    <div class={clsx('flex flex-col items-start justify-center min-w-0', CELL_PADDING, props.class)}>
        <span class="body-xs font-cvi-bold text-cvi-fg-default break-words">{props.children}</span>
    </div>
);

const DataCell = (props: { children?: JSX.Element | string; class?: string }) => (
    <div class={clsx('flex items-center min-w-0', CELL_PADDING, props.class)}>
        <span class="body-sm font-cvi-regular text-cvi-fg-default break-words">{props.children}</span>
    </div>
);

export const ProductSpecifications = (props: ProductSpecificationProps) => {
    const { localize, viewport, siteInfo } = useContext(AppContext);

    const isCareSite = () => siteInfo.siteType === 'atos-care';

    const mobileBreakpoint = () => {
        if (isCareSite()) return viewport.width <= theme.breakpoints.DESKTOP;

        return viewport.width <= theme.breakpoints.ORIGINAL_TABLET;
    };

    const specifications = () => props.product.specifications ?? [];
    const lastIndex = () => specifications().length - 1;

    return (
        <Show when={mobileBreakpoint()} fallback={
            <div class="rounded-cvi-md bg-cvi-bg-neutral-subtle overflow-hidden">
                <div
                    class="grid"
                    classList={{
                        'grid-cols-[repeat(4,auto)]': !isCareSite(),
                        'grid-cols-[repeat(5,auto)]': isCareSite(),
                    }}
                >
                    <HeaderCell class="border-b border-cvi-border-default">{localize('name', 'Name')}</HeaderCell>
                    <HeaderCell class="border-b border-cvi-border-default">{localize('description', 'Description')}</HeaderCell>
                    <HeaderCell class="border-b border-cvi-border-default">{localize('ref-nr', 'Reference number')}</HeaderCell>
                    <HeaderCell class="border-b border-cvi-border-default">{localize('quantity', 'Quantity')}</HeaderCell>
                    <Show when={isCareSite()}>
                        <HeaderCell class="border-b border-cvi-border-default" />
                    </Show>

                    <For each={specifications()}>
                        {(spec: Specification, idx) => {
                            const rowBorder = () => (idx() !== lastIndex() ? 'border-b border-cvi-border-default' : '');
                            return (
                                <>
                                    <DataCell class={rowBorder()}>{spec?.name}</DataCell>
                                    <DataCell class={rowBorder()}>{spec?.description}</DataCell>
                                    <DataCell class={rowBorder()}>{spec?.refNumber}</DataCell>
                                    <DataCell class={rowBorder()}>{spec?.quantity}</DataCell>
                                    <Show when={isCareSite()}>
                                        <DataCell class={rowBorder()}>
                                            <AddToBasketButton product={props.product} specification={spec} />
                                        </DataCell>
                                    </Show>
                                </>
                            );
                        }}
                    </For>
                </div>
            </div>
        }>
            <ProductSpecificationsMobile product={props.product} />
        </Show>
    );
};
